import { endpoint } from "./utils";
import { scopes } from "@repo/shared/config";
import axios from 'axios';


export const getToken = async (msalInstance, msalAccounts, isAuthenticated) => {
    const response = await msalInstance.acquireTokenSilent({
        scopes: scopes,
    });
    if (!response.idToken || response.expiresOn < Date.now()) {
        throw new Error("No token returned from acquireTokenSilent or token has expired");
    }
    console.log("Token is valid, expires on:", response.expiresOn);
    return response.idToken;
};

// Fallback step-up handler for clients created without an explicit
// onStepUp. PolyAuthProvider registers its stepUp() here so EVERY client
// from this factory handles step_up_required 403s — coverage must not
// depend on each call site remembering to opt in.
let defaultStepUpHandler = null;
export const setDefaultStepUpHandler = (handler) => {
  defaultStepUpHandler = handler;
  return () => {
    if (defaultStepUpHandler === handler) defaultStepUpHandler = null;
  };
};

export const get_api_client = ({baseUrl, token, sessionToken, appContext, accountId, on401, onStepUp} = {}) => {
  const effectiveOn401 = on401 || null;

  const client = axios.create({
    baseURL: baseUrl || `https://${endpoint()}`,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...(sessionToken && { 'X-PG-Auth': `Bearer ${sessionToken}` }),
      ...(appContext && { 'X-Zoom-App-Context': appContext }),
      ...(accountId && { 'X-Zoom-Account-Id': accountId }),
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0'
    }
  });

  client.interceptors.request.use(
    function (config) {
      // Cache-busting timestamp param on every request.
      config.params = { timestamp: Date.now(), ...config.params || {} };
      return config;
    },
    function (error) {
      return Promise.reject(error);
    }
  );

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Privileged endpoints reject email-assurance sessions with a 403
      // step-up marker. Elevate the session (QR verification -> new cert
      // session) and retry the original request exactly once. The step_up
      // call itself is excluded so a misbehaving backend can't recurse.
      // The default handler is resolved at error time, so clients created
      // before PolyAuthProvider registered it are still covered.
      const stepUpHandler = onStepUp || defaultStepUpHandler;
      if (
        error.response?.status === 403 &&
        error.response?.data?.detail?.code === 'step_up_required' &&
        stepUpHandler &&
        originalRequest &&
        !originalRequest._stepUpRetried &&
        !originalRequest.url?.includes('/v2/session/step_up')
      ) {
        originalRequest._stepUpRetried = true;
        try {
          const newSessionToken = await stepUpHandler();
          originalRequest.headers = {
            ...originalRequest.headers,
            'X-PG-Auth': `Bearer ${newSessionToken}`,
          };
          return client(originalRequest);
        } catch (stepUpError) {
          // Cancelled or failed verification. The 403's `detail` is the
          // step_up object; hooks that render response.data.detail would show
          // "[object Object]". Replace it with a readable string and keep the
          // code so callers can localize or treat cancel quietly.
          if (error.response?.data && typeof error.response.data.detail === 'object') {
            error.response.data = {
              ...error.response.data,
              detail: 'Identity verification is required to make this change.',
              code: 'step_up_required',
            };
          }
          error.code = 'step_up_required';
          return Promise.reject(error);
        }
      }

      if (error.response?.status === 401) {
        console.log('Caught 401 in axios interceptor');
        originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;

        if (effectiveOn401) {
          try {
            await effectiveOn401();
            return Promise.reject(error);
          } catch (retryError) {
            return Promise.reject(retryError);
          }
        }
      }

      return Promise.reject(error);
    }
  );

  return client;
};
